'use client';

import { useEffect, useRef, useState } from 'react';
import { SECTION_LABEL_RE } from '@/entities/blog';
import styles from './BlogTOC.module.scss';

// 고정 GNB 높이 (tocCard sticky top, h2 scroll-margin-top 과 동일 기준)
const HEADER_OFFSET = 92;
// 스크롤 도착 시 섹션 카드 상단이 GNB 아래 이만큼 떨어지도록
const SCROLL_OFFSET = HEADER_OFFSET + 12;
// 이 선(뷰포트 상단 기준)을 지난 섹션 중 마지막 것을 현재 섹션으로 본다
const ACTIVE_LINE = SCROLL_OFFSET + 8;
// 클릭 스크롤 중 스파이 잠금 해제 폴백 (smooth scroll 최대 소요 대비)
const AUTO_SCROLL_TIMEOUT_MS = 1500;

type TocSection = { id: string; label: string | null; title: string };

// h2 는 rehype-sectionize 가 감싼 <section> 카드 안에 있다.
// 스크롤 목표/활성 판정 모두 카드 상단 기준으로 해야 시각적 위치와 일치한다.
function sectionElOf(id: string): HTMLElement | null {
  const heading = document.getElementById(id);
  return (heading?.closest('section') as HTMLElement | null) ?? heading;
}

function findScrollContainer(el: HTMLElement): HTMLElement | null {
  let parent: HTMLElement | null = el.parentElement;
  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    const canScroll = overflowY === 'auto' || overflowY === 'scroll';
    if (canScroll && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
}

function scrollToId(id: string) {
  const target = sectionElOf(id);
  if (!target) return;
  const container = findScrollContainer(target);
  if (container) {
    const targetRect = target.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const top =
      targetRect.top - containerRect.top + container.scrollTop - SCROLL_OFFSET;
    container.scrollTo({ top, behavior: 'smooth' });
  } else {
    const top =
      target.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}

// MDX 본문의 h2(rehype-slug 가 id 부여)를 마운트 후 수집해 목차를 만들고,
// 스크롤 위치에 따라 현재 섹션 하이라이트와 URL 해시를 동기화한다.
export function BlogTOC() {
  const [sections, setSections] = useState<TocSection[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  // 클릭/해시 진입으로 시작된 자동 스크롤 — 완료 전까지 스파이가 덮어쓰지 않게 잠금
  const autoScrollRef = useRef<{ id: string; until: number } | null>(null);
  const hashRef = useRef<string | null>(null);

  useEffect(() => {
    const headings =
      document.querySelectorAll<HTMLHeadingElement>('article h2[id]');
    setSections(
      Array.from(headings).map((heading) => {
        const text = heading.textContent ?? '';
        const match = text.match(SECTION_LABEL_RE);
        return {
          id: heading.id,
          label: match ? match[1] : null,
          title: match ? match[2] : text,
        };
      }),
    );
  }, []);

  function beginAutoScroll(id: string) {
    autoScrollRef.current = { id, until: Date.now() + AUTO_SCROLL_TIMEOUT_MS };
    hashRef.current = id;
    setActiveId(id);
    scrollToId(id);
  }

  // 스크롤 스파이: ACTIVE_LINE 위로 지나간 마지막 섹션 카드를 활성으로 표시
  useEffect(() => {
    if (sections.length === 0) return;
    let raf = 0;

    const update = () => {
      raf = 0;

      // 자동 스크롤 진행 중이면 목표 도달(또는 타임아웃) 전까지 판정 보류
      const auto = autoScrollRef.current;
      if (auto) {
        const target = sectionElOf(auto.id);
        const reached =
          target &&
          Math.abs(target.getBoundingClientRect().top - SCROLL_OFFSET) < 4;
        if (!reached && Date.now() < auto.until) return;
        autoScrollRef.current = null;
      }

      let current: string | null = null;
      for (const section of sections) {
        const el = sectionElOf(section.id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= ACTIVE_LINE) {
          current = section.id;
        } else {
          break;
        }
      }
      setActiveId(current);

      // URL 해시를 현재 섹션에 즉시 동기화 (replaceState → 히스토리 오염 없음)
      if (current !== hashRef.current) {
        hashRef.current = current;
        const { pathname, search } = window.location;
        history.replaceState(
          null,
          '',
          current ? `#${current}` : `${pathname}${search}`,
        );
      }
    };

    const handleScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    update();
    // capture: window 외 내부 스크롤 컨테이너의 scroll 이벤트도 수신
    document.addEventListener('scroll', handleScroll, {
      capture: true,
      passive: true,
    });
    return () => {
      document.removeEventListener('scroll', handleScroll, { capture: true });
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sections]);

  useEffect(() => {
    const consumeHash = (hash: string) => {
      const id = decodeURIComponent(
        hash.startsWith('#') ? hash.slice(1) : hash,
      );
      if (!id || !document.getElementById(id)) return;
      autoScrollRef.current = {
        id,
        until: Date.now() + AUTO_SCROLL_TIMEOUT_MS,
      };
      hashRef.current = id;
      setActiveId(id);
      scrollToId(id);
    };

    if (window.location.hash) {
      consumeHash(window.location.hash);
    }

    const handleHashChange = () => {
      consumeHash(window.location.hash);
    };

    const handleAnchorClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      if (event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      const anchor = (event.target as HTMLElement | null)?.closest('a');
      if (!anchor) return;
      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('#')) return;
      const id = decodeURIComponent(href.slice(1));
      if (!id) return;
      if (!document.getElementById(id)) return;
      event.preventDefault();
      history.pushState(null, '', `#${id}`);
      consumeHash(id);
    };

    window.addEventListener('hashchange', handleHashChange);
    document.addEventListener('click', handleAnchorClick);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      document.removeEventListener('click', handleAnchorClick);
    };
  }, []);

  function handleButtonClick(id: string) {
    history.replaceState(null, '', `#${id}`);
    beginAutoScroll(id);
  }

  return (
    <nav className={styles.nav}>
      {sections.map((section) => (
        <button
          key={section.id}
          type="button"
          onClick={() => handleButtonClick(section.id)}
          className={
            section.id === activeId
              ? `${styles.button} ${styles.buttonActive}`
              : styles.button
          }
        >
          {section.label ? (
            <span className={styles.label}>{section.label}</span>
          ) : null}
          {section.title}
        </button>
      ))}
    </nav>
  );
}
